import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ProjectType } from "@/consts/Bid";
import { DEFAULT_PAGINATION } from "@/consts/common";
import { useReadProjectGroupsAll } from "@/libs/react-query/hooks/useProjectQuery";
import { cn } from "@/libs/utils";
import TaskStatus from "@/pages/Project/Management/columns/components/TaskStatus";
import { parseProjectType } from "@/pages/Project/utils";
import { ProjectGroup } from "@/types/project";
import { User } from "@/types/user";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ChevronDown, Plus } from "lucide-react";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

export default function RedirectLinkedProject() {
  const { projectType, projectId } = useParams();

  const navigate = useNavigate();

  const { data } = useReadProjectGroupsAll({
    project: {
      project_id: projectId || "",
      project_type: parseProjectType(projectType as ProjectType),
    },
    pagination: DEFAULT_PAGINATION,
  });

  const dataWithClickHandlers = useMemo(() => {
    return (
      data?.project_groups.map((item) => ({
        ...item,
        onRowClick: () =>
          navigate(`/management/projects/${item.project_group_id}`),
        disabled: !item.is_participating,
      })) || []
    );
  }, [data, navigate]);

  type ProjectGroupWithHandlers = ProjectGroup & {
    disabled: boolean;
    onRowClick?: () => void;
  };

  const columns: ColumnDef<ProjectGroupWithHandlers>[] = [
    {
      accessorKey: "project_group_title",
      header: "프로젝트명",
      cell: ({ row }) => (
        <span className="text-gray-A text-sm font-normal">
          {row.getValue("project_group_title") as string}
        </span>
      ),
      size: 510,
    },
    {
      accessorKey: "users",
      header: "참여자",
      cell: ({ row }) => {
        const users = row.getValue("users") as User[];
        return (
          <div className="flex items-center gap-4">
            {users.slice(0, 2).map((user, index) => (
              <div
                className="flex items-center gap-2"
                key={`users-${user.user_id}-${index}`}
              >
                <Avatar
                  key={`users-${user.user_id}-${index}`}
                  className={`size-4 ${
                    index === 0
                      ? "z-30"
                      : index === 1
                        ? "z-20 -ml-1"
                        : "z-10 -ml-1"
                  }`}
                >
                  <AvatarImage src={user.img_url} />
                  <AvatarFallback>{user.user_name}</AvatarFallback>
                </Avatar>
                <span className="text-gray-A text-xs font-normal">
                  {user.user_name}
                </span>
              </div>
            ))}
            {users.length > 2 && (
              <div className="flex items-center text-gray-B">
                <Plus className="size-3.5" />
                <span className="text-sm font-semibold">
                  {users.length - 2}
                </span>
              </div>
            )}
          </div>
        );
      },
      size: 232,
    },
    {
      accessorKey: "task_status_map",
      header: "작업 진행률",
      cell: ({ row }) => (
        <TaskStatus
          taskStatusMap={row.getValue("task_status_map")}
          isParticipating={row.original.is_participating}
        />
      ),
      size: 91,
    },
  ];

  const table = useReactTable({
    data: dataWithClickHandlers || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (data?.total_count === 0) {
    return <></>;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="py-[6px] px-3 bg-green text-sm text-white font-semibold rounded-sm hover:bg-green/80"
        >
          연결된 프로젝트 보기
          <ChevronDown className="w-4 h-4 ml-1 inline-block" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-3 w-[880px] max-h-[300px] overflow-y-auto border-2 border-green rounded-xl">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      style={{
                        minWidth: header.column.columnDef.size,
                        maxWidth: header.column.columnDef.size,
                      }}
                      className="px-3"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className={cn(
                    typeof row.original.onRowClick === "function" &&
                      !row.original.disabled &&
                      "cursor-pointer",
                    row.original.disabled && "opacity-50 bg-light-gray"
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    if (
                      typeof row.original.onRowClick === "function" &&
                      !row.original.disabled
                    ) {
                      row.original.onRowClick();
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      style={{
                        minWidth: cell.column.columnDef.size,
                        maxWidth: cell.column.columnDef.size,
                      }}
                      className="py-2 px-3"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <div className="flex items-center justify-center h-full text-gray-A text-sm font-normal">
                    데이터가 없습니다.
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </PopoverContent>
    </Popover>
  );
}
